import { useState } from "react";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EccObservationPreset, EccObservations } from "@/api/types";

type Props = {
  presets: EccObservationPreset[];
  custom: string[];
  onChange: (next: EccObservations) => void;
};

export function ObservationsBlock({ presets, custom, onChange }: Props) {
  const [inputValue, setInputValue] = useState("");

  function togglePreset(code: string) {
    const next = presets.map((p) =>
      p.code === code ? { ...p, checked: !p.checked } : p,
    );
    onChange({ presets: next, custom: custom.map((label) => ({ label })) });
  }

  function addCustom() {
    const trimmed = inputValue.trim();
    if (!trimmed || custom.includes(trimmed)) return;
    onChange({
      presets,
      custom: [...custom.map((label) => ({ label })), { label: trimmed }],
    });
    setInputValue("");
  }

  function removeCustom(label: string) {
    onChange({
      presets,
      custom: custom.filter((l) => l !== label).map((l) => ({ label: l })),
    });
  }

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <p className="text-sm font-medium">Puntos a analizar</p>
        <p className="text-xs text-muted-foreground">
          Selecciona los criterios que aplican a esta corrida. Los desmarcados no se evaluarán.
        </p>
      </div>

      {presets.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Predeterminadas
          </p>
          <div className="grid grid-cols-2 gap-2">
            {presets.map((p) => (
              <label
                key={p.code}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
                  p.checked
                    ? "border-primary/40 bg-primary/5"
                    : "bg-muted/40 text-muted-foreground",
                )}
              >
                <input
                  type="checkbox"
                  checked={p.checked}
                  onChange={() => togglePreset(p.code)}
                  className="accent-primary"
                />
                <span className="font-semibold text-xs">{p.code}</span>
                <span className="truncate">{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Personalizadas
        </p>
        {custom.length > 0 && (
          <div className="mb-2 space-y-1">
            {custom.map((label) => (
              <div
                key={label}
                className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 text-sm"
              >
                <span>{label}</span>
                <button
                  type="button"
                  onClick={() => removeCustom(label)}
                  className="ml-2 text-muted-foreground hover:text-destructive"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustom()}
            placeholder="Agregar otro rubro…"
            maxLength={200}
            className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!inputValue.trim()}
            className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-40 hover:bg-accent"
          >
            <Plus className="size-3.5" />
            Agregar
          </button>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Los rubros personalizados quedan registrados en el log de auditoría de esta corrida (AP-12).
      </p>
    </div>
  );
}
